<svelte:options customElement="hb-input-number" />

<script lang="ts">
  /**
   * Svelte Input Number WebComponent
   * =====================
   *
   * @contributors: Dario Caruso <dev@dariocaruso.info> (https://dariocaruso.info)
   *
   * @license: MIT License
   *
   */

  import type { Component } from "./types/webcomponent.type";
  import {
    parseSchemaentryProp,
    sameSetValPayload,
    schemaentryFingerprint,
    shouldApplySchemaSync,
  } from "../lib/schemaentry";

  let {
    schemaentry,
    show_validation = "no",
    is_small = "no",
  }: Component = $props();

  let field = $state<Record<string, any> | null>(null);
  let schemaFingerprint = $state("");

  let value: number | undefined = $state(undefined);
  let min: number = $derived.by(() => {
    return field?.params?.min || field?.params?.min === 0
      ? field.params.min
      : 0;
  });
  let max: number = $derived.by(() => {
    return field?.params?.max || field?.params?.max === 0
      ? field.params.max
      : 0;
  });

  let hasMinParam = $derived(
    field?.params != null &&
      Object.prototype.hasOwnProperty.call(field.params, "min"),
  );
  let hasMaxParam = $derived(
    field?.params != null &&
      Object.prototype.hasOwnProperty.call(field.params, "max"),
  );

  let valid = $derived.by(() => {
    if (field) {
      if (field.required) {
        if (value !== undefined && value !== null) {
          if (min && !(value >= min)) {
            return false;
          } else if (max && !(value <= max)) {
            return false;
          } else {
            return true;
          }
        } else {
          return false;
        }
      } else {
        return true;
      }
    } else {
      return false;
    }
  });

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }

  $effect(() => {
    const fp = schemaentryFingerprint(schemaentry);
    const nextField = parseSchemaentryProp(schemaentry);
    if (!nextField) {
      field = null;
      return;
    }
    if (shouldApplySchemaSync(fp, () => schemaFingerprint)) {
      schemaFingerprint = fp;
      field = nextField;
      if (nextField.value !== undefined && nextField.value !== null) {
        const n = Number(nextField.value);
        value = Number.isNaN(n) ? undefined : n;
      } else {
        value = undefined;
      }
    }
  });

  let lastSetVal = $state<{
    value: unknown;
    valid: boolean;
    id: string | undefined;
  } | null>(null);

  $effect(() => {
    const f = field;
    if (!f?.id) return;
    const payload = { value, valid, id: f.id as string | undefined };
    if (sameSetValPayload(lastSetVal, payload)) return;
    lastSetVal = payload;
    dispatch("setVal", { value, valid, id: f.id });
  });

  function onEnterKey(e: KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      dispatch("clickEnter", { value, valid, id: field?.id });
    }
  }
</script>

{#if field != null}
  <div class="field">
    <div class="control">
      <input
        bind:value
        type="number"
        class="input"
        class:is-small={is_small === "yes"}
        class:is-success={show_validation === "yes" &&
          !!field?.required &&
          valid}
        class:is-danger={show_validation === "yes" &&
          !!field?.required &&
          !valid}
        id={field?.id}
        required={field?.required}
        placeholder={field?.placeholder}
        readonly={field?.readonly}
        disabled={!!field?.disabled}
        min={hasMinParam ? min : undefined}
        max={hasMaxParam ? max : undefined}
        onkeypress={!hasMinParam && !hasMaxParam ? onEnterKey : undefined}
      />
    </div>

    {#if field?.validationTip && show_validation === "yes" && !valid}
      <p part="invalid-feedback" class="help is-danger">
        {field.validationTip}
      </p>
    {/if}
  </div>
{/if}

<style lang="scss">
  @forward "./styles/bulma.scss";
  @forward "./styles/webcomponent.scss";
</style>
